/**
 * A8 · Studios (owner / teacher / front-desk),
 * E7 studio analytics, F5 studio-wide announcements, B8 shared library.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Select,
  Spinner,
  Tag,
  Textarea,
  fmtMoney,
} from '../../lib/ui.tsx';

interface Membership {
  role: 'owner' | 'teacher' | 'front_desk' | string;
  studio: {
    id: string;
    name: string;
    brandColor: string;
    members: { role: string; user: { id: string; name: string; email: string } }[];
  };
}

interface StudioAnalytics {
  perTeacher: {
    teacher?: { id: string; name: string };
    activeStudents: number;
    practicedThisWeek: number;
    atRisk: number;
    lessonsLast30d: number;
    revenueLast30dCents: number;
  }[];
}

export default function StudioPage() {
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#D85A30');

  const memberships = useQuery({
    queryKey: ['studios-mine'],
    queryFn: () => api<Membership[]>('/api/studios/mine'),
  });

  const createStudio = useMutation({
    mutationFn: () => api('/api/studios', { body: { name: newName.trim(), brandColor: newColor } }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['studios-mine'] });
    },
    onError: setError,
  });

  if (memberships.isLoading) return <Spinner />;

  const mine = memberships.data ?? [];
  const owned = mine.find((m) => m.role === 'owner');
  const others = mine.filter((m) => m.role !== 'owner');

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">Studio</h1>

      {mine.length === 0 && (
        <Card title="Create your studio">
          <p className="mb-3 text-xs text-slate-500">
            Run a multi-teacher studio: shared exercise library, per-teacher rosters and studio-wide
            announcements.
          </p>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <Field label="Studio name">
              <Input placeholder="e.g. Studio Cantabile" value={newName} onChange={(e) => setNewName(e.target.value)} />
            </Field>
            <Field label="Brand color">
              <Input type="color" className="h-9 w-16 p-1" value={newColor} onChange={(e) => setNewColor(e.target.value)} />
            </Field>
            <Button disabled={!newName.trim() || createStudio.isPending} onClick={() => createStudio.mutate()}>
              Create studio
            </Button>
          </div>
          <ErrorText error={error} />
        </Card>
      )}

      {owned && <OwnerPanel membership={owned} />}

      {others.map((m) => (
        <Card key={m.studio.id} title={m.studio.name}>
          <div className="flex items-center gap-2">
            <span className="inline-block size-4 rounded-full" style={{ backgroundColor: m.studio.brandColor }} />
            <Tag tone="brand">{m.role.replace('_', ' ')}</Tag>
            <span className="text-xs text-slate-500">{m.studio.members.length} members</span>
          </div>
          <ul className="mt-3 space-y-1">
            {m.studio.members.map((mem) => (
              <li key={mem.user.id} className="flex items-center justify-between text-sm">
                <span>
                  {mem.user.name} <span className="text-xs text-slate-400">{mem.user.email}</span>
                </span>
                <Tag>{mem.role.replace('_', ' ')}</Tag>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-slate-500">
            Exercises shared with the studio appear in your{' '}
            <Link to="/coach/exercises" className="font-medium text-brand-700 hover:underline">
              exercise library
            </Link>
            .
          </p>
        </Card>
      ))}

      {mine.length > 0 && !owned && others.length === 0 && (
        <EmptyState title="No studio memberships" hint="Ask a studio owner to add you by email." />
      )}
    </div>
  );
}

function OwnerPanel({ membership }: { membership: Membership }) {
  const studio = membership.studio;
  const qc = useQueryClient();
  const [error, setError] = useState<unknown>(null);

  const [name, setName] = useState(studio.name);
  const [color, setColor] = useState(studio.brandColor);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'teacher' | 'front_desk'>('teacher');
  const [announcement, setAnnouncement] = useState('');
  const [announceResult, setAnnounceResult] = useState<number | null>(null);

  const analytics = useQuery({
    queryKey: ['studio-analytics', studio.id],
    queryFn: () => api<StudioAnalytics>(`/api/studios/${studio.id}/analytics`),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['studios-mine'] });

  const patchStudio = useMutation({
    mutationFn: () => api(`/api/studios/${studio.id}`, { method: 'PATCH', body: { name: name.trim(), brandColor: color } }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: setError,
  });

  const addMember = useMutation({
    mutationFn: () =>
      api(`/api/studios/${studio.id}/members`, { body: { email: inviteEmail.trim().toLowerCase(), role: inviteRole } }),
    onSuccess: () => {
      setError(null);
      setInviteEmail('');
      invalidate();
    },
    onError: setError,
  });

  const removeMember = useMutation({
    mutationFn: (userId: string) => api(`/api/studios/${studio.id}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: setError,
  });

  const announce = useMutation({
    mutationFn: () => api<{ recipients: number }>(`/api/studios/${studio.id}/announcements`, { body: { body: announcement.trim() } }),
    onSuccess: (res) => {
      setError(null);
      setAnnouncement('');
      setAnnounceResult(res.recipients);
    },
    onError: setError,
  });

  const teachers = studio.members.filter((m) => m.role !== 'front_desk');

  return (
    <div className="space-y-4">
      <Card
        title={studio.name}
        actions={<Tag tone="brand">owner</Tag>}
      >
        <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Field label="Studio name">
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Brand color">
            <Input type="color" className="h-9 w-16 p-1" value={color} onChange={(e) => setColor(e.target.value)} />
          </Field>
          <Button variant="secondary" disabled={!name.trim() || patchStudio.isPending} onClick={() => patchStudio.mutate()}>
            Save
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          B8 · Teachers can share exercises with the whole studio from the{' '}
          <Link to="/coach/exercises" className="font-medium text-brand-700 hover:underline">
            exercise library
          </Link>
          .
        </p>
      </Card>

      <Card title="Members">
        <ul className="space-y-2">
          {studio.members.map((m) => (
            <li key={m.user.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{m.user.name}</p>
                <p className="truncate text-xs text-slate-500">{m.user.email}</p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Tag tone={m.role === 'owner' ? 'brand' : 'default'}>{m.role.replace('_', ' ')}</Tag>
                {m.role !== 'owner' && (
                  <Button variant="ghost" className="px-2 py-1 text-xs" disabled={removeMember.isPending} onClick={() => removeMember.mutate(m.user.id)}>
                    Remove
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <Field label="Add member by email" hint="They need an existing Solfo coach account.">
            <Input type="email" placeholder="teacher@studio.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
          </Field>
          <Field label="Role">
            <Select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'teacher' | 'front_desk')}>
              <option value="teacher">Teacher</option>
              <option value="front_desk">Front desk</option>
            </Select>
          </Field>
          <Button disabled={!inviteEmail.trim() || addMember.isPending} onClick={() => addMember.mutate()}>
            Add
          </Button>
        </div>
      </Card>

      <Card title="Studio analytics (last 30 days)">
        {analytics.isLoading ? (
          <Spinner />
        ) : !analytics.data || analytics.data.perTeacher.length === 0 ? (
          <p className="text-xs text-slate-500">No teacher activity yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-xs text-slate-500">
                  <th className="py-2 pr-3 font-medium">Teacher</th>
                  <th className="py-2 pr-3 text-right font-medium">Active students</th>
                  <th className="py-2 pr-3 text-right font-medium">Practiced this week</th>
                  <th className="py-2 pr-3 text-right font-medium">At risk</th>
                  <th className="py-2 pr-3 text-right font-medium">Lessons (30d)</th>
                  <th className="py-2 text-right font-medium">Revenue (30d)</th>
                </tr>
              </thead>
              <tbody>
                {analytics.data.perTeacher.map((t, i) => (
                  <tr key={t.teacher?.id ?? i} className="border-b border-slate-100">
                    <td className="py-2 pr-3 font-medium">{t.teacher?.name ?? '—'}</td>
                    <td className="py-2 pr-3 text-right">{t.activeStudents}</td>
                    <td className="py-2 pr-3 text-right">{t.practicedThisWeek}</td>
                    <td className="py-2 pr-3 text-right">
                      {t.atRisk > 0 ? <Tag tone="amber">{t.atRisk}</Tag> : <span className="text-slate-400">0</span>}
                    </td>
                    <td className="py-2 pr-3 text-right">{t.lessonsLast30d}</td>
                    <td className="py-2 text-right font-medium">{fmtMoney(t.revenueLast30dCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Studio announcement">
        <Textarea
          rows={3}
          placeholder="e.g. The studio is closed over the holidays — recital on Jan 14!"
          value={announcement}
          onChange={(e) => setAnnouncement(e.target.value)}
        />
        <div className="mt-2 flex items-center gap-3">
          <Button disabled={!announcement.trim() || announce.isPending} onClick={() => announce.mutate()}>
            Send to all studio students
          </Button>
          {announceResult !== null && <Tag tone="green">reached {announceResult} students</Tag>}
        </div>
      </Card>

      <ErrorText error={error} />
    </div>
  );
}
